import { ReactElement, useMemo } from 'react';
import { alpha } from '@mui/material';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';

import { useGetStatsQuery } from '../../actions/getStats';
import { EpochTimer } from './EpochTimer';
import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

export function Stats(): ReactElement | null {
  const { classes } = useStyles();
  const { t, keys } = useTranslation(mergedTranslation);

  const { data } = useGetStatsQuery();

  const tvl = data?.tvl;
  const rawTotalApy = data?.apyTotal;
  const apyTotal = useMemo(
    () =>
      rawTotalApy &&
      new BigNumber(rawTotalApy)
        .decimalPlaces(2, BigNumber.ROUND_HALF_EVEN)
        .toNumber(),
    [rawTotalApy],
  );
  const stakers = data?.stakers;

  return (
    <div className={classes.root}>
      <div className={classes.item}>
        <div className={classes.title}>{t(keys.tvl)}</div>

        <div className={classes.value}>
          {tvl
            ? t(keys.unit.$, { value: t(keys.unit.compact, { value: tvl }) })
            : t(keys.common.none)}
        </div>
      </div>

      <div className={classes.item}>
        <div className={classes.title}>{t(keys.apy)}</div>

        <div className={classes.value}>
          {apyTotal
            ? t(keys.unit.percent, { value: apyTotal })
            : t(keys.common.none)}
        </div>
      </div>

      <div className={classes.item}>
        <div className={classes.title}>{t(keys.stakers)}</div>

        <div className={classes.value}>
          {stakers
            ? t(keys.unit.compact, { value: stakers })
            : t(keys.common.none)}
        </div>
      </div>

      <div className={classes.item}>
        <div className={classes.title}>{t(keys.epoch)}</div>

        <div className={classes.value}>
          <EpochTimer />
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    gap: theme.spacing(2),
    scrollbarWidth: 'none',

    [theme.breakpoints.up('sm')]: {
      borderRadius: 12,
      padding: theme.spacing(3.75, 6),
      background: `linear-gradient(110.01deg, ${theme.palette.background.default} 8.7%, ${theme.palette.primary.contrastText} 40.24%)`,

      '&:before': {
        display: 'block',
        content: '""',
        position: 'absolute',
        inset: '0',
        borderRadius: 12,
        padding: theme.spacing(0.5),
        background: `linear-gradient(170deg, ${theme.palette.secondary.light} 3.74%, ${theme.palette.primary.main} 7.03%, ${theme.palette.secondary.main} 12.22%, ${theme.palette.secondary.dark} 25.76%, ${theme.palette.background.default} 41.68%)`,
        mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        maskComposite: 'exclude',
      },
    },

    [theme.breakpoints.down('sm')]: {
      overflowX: 'auto',
      marginRight: theme.spacing(-4),
      paddingRight: theme.spacing(4),
    },
  },
  item: {
    textAlign: 'center',
    width: '25%',
    minWidth: 170,

    [theme.breakpoints.up('sm')]: {
      '&:not(:last-of-type)': {
        borderRight: `1px solid ${theme.palette.background.default}`,
      },
    },

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2.5),
      position: 'relative',
      borderRadius: 12,
      background: `linear-gradient(110.01deg, ${theme.palette.background.default} 8.7%, ${theme.palette.primary.contrastText} 40.24%)`,

      '&:before': {
        display: 'block',
        content: '""',
        position: 'absolute',
        inset: '0',
        borderRadius: 12,
        padding: theme.spacing(0.5),
        background: `linear-gradient(170deg, ${theme.palette.secondary.light} 3.74%, ${theme.palette.primary.main} 7.03%, ${theme.palette.secondary.main} 12.22%, ${theme.palette.secondary.dark} 25.76%, ${theme.palette.background.default} 41.68%)`,
        mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        maskComposite: 'exclude',
      },
    },
  },
  title: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.2,
    color: alpha(theme.palette.secondary.contrastText, 0.5),

    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(12),
    },
  },
  value: {
    fontSize: theme.typography.pxToRem(20),
    lineHeight: 1.2,
    color: theme.palette.secondary.contrastText,
    marginTop: theme.spacing(1),
    fontWeight: 600,
  },
}));
